import { log, supabase } from ".";

/**
 * Escritor ÚNICO de `clientes_contactos_cliente`/`clientes_contactos_agencia` (10.229 — Proyecto
 * Contactos, Fase 1 "dejar de destruir"). Reemplaza los `removeWhere` por `cliente_id` entero +
 * re-insert que tenían las 3 pantallas de edición (V1/V2/ClientesListado): esas pantallas solo
 * muestran 4 casilleros por lado, así que un `removeWhere(cliente_id=eq...)` borraba TAMBIÉN el
 * 5º+ contacto que la pantalla nunca cargó, y en el lado agencia además el contacto OMNET que
 * administra Mapa de Calor aparte (`TablaCalorAgencias.js`).
 *
 * Regla de oro: una pantalla de N casilleros solo puede tocar lo que ELLA mostró. `antes`/`despues`
 * son los `usuario_id` que la pantalla tenía cargados en sus casilleros antes y después de la
 * edición — nunca "todo lo que hay en la tabla hoy". El diff calcula:
 *   - **Borrar:** ids en `antes` y no en `despues` — Y que sigan existiendo de verdad en la tabla
 *     (protege contra ediciones concurrentes: no falla si alguien ya lo sacó).
 *   - **Insertar:** ids en `despues` que la tabla todavía NO tiene para ese cliente (con
 *     cualquier cargo — evita reintentar un INSERT que violaría el UNIQUE(cliente_id,usuario_id)).
 * Nunca se recalculan ni se tocan los ids que ni `antes` ni `despues` mencionan.
 *
 * Lado agencia: las filas con `cargo === "OMNET"` quedan SIEMPRE fuera del cálculo de borrado
 * (ni siquiera se consideran "existentes" para ese cálculo) — mismo criterio que ya usa
 * `TablaCalorAgencias.js` con su propio `cargo=neq.${OMNET_CARGO}`. Doble cinturón: el filtro de
 * DELETE en sí también lleva `cargo=neq.OMNET`, así que ni un id que por error apareciera en
 * `antes` podría borrar la fila OMNET de ese usuario.
 *
 * Cargo al insertar: el `usuarios.cargo` REAL de esa persona — nunca un cargo inventado por
 * posición ("Director"/"Supervisor"/"Planificador"/"Asistente"/"Gerente de Marketing"/"Jefe de
 * Medios", que es lo que hacían las 4 escrituras que esto reemplaza). `cargo` es `NOT NULL` en las
 * dos tablas (verificado contra el schema real) — si `usuarios.cargo` viene `null`/vacío, se
 * inserta `""` en vez de `null` (única forma de no violar el NOT NULL).
 */
const TABLA_POR_LADO = {
  cliente: "clientes_contactos_cliente",
  agencia: "clientes_contactos_agencia",
};

export const OMNET_CARGO = "OMNET";

const aIdsNumericos = (lista) => new Set((lista || []).filter((id) => id != null && id !== "").map((id) => Number(id)));

export const sincronizarContactosCliente = async ({ clienteId, lado, antes, despues }) => {
  const tabla = TABLA_POR_LADO[lado];
  if (!tabla) throw new Error(`sincronizarContactosCliente: lado inválido "${lado}"`);
  if (!clienteId) throw new Error("sincronizarContactosCliente: falta clienteId");

  const antesIds = aIdsNumericos(antes);
  const despuesIds = aIdsNumericos(despues);

  const filasActuales = (await supabase.select(tabla, { filters: { cliente_id: Number(clienteId) } })) || [];
  const existentesIds = new Set(filasActuales.map((f) => Number(f.usuario_id)));
  const existentesNoOmnetIds =
    lado === "agencia"
      ? new Set(filasActuales.filter((f) => f.cargo !== OMNET_CARGO).map((f) => Number(f.usuario_id)))
      : existentesIds;

  const aBorrar = [...antesIds].filter((id) => !despuesIds.has(id) && existentesNoOmnetIds.has(id));
  const aInsertar = [...despuesIds].filter((id) => !existentesIds.has(id));

  if (aBorrar.length > 0) {
    try {
      const guardOmnet = lado === "agencia" ? `&cargo=neq.${OMNET_CARGO}` : "";
      await supabase.removeWhere(tabla, `cliente_id=eq.${clienteId}&usuario_id=in.(${aBorrar.join(",")})${guardOmnet}`);
    } catch (error) {
      log.error("sincronizarContactosCliente: borrado", error);
      throw error;
    }
  }

  if (aInsertar.length > 0) {
    try {
      const filas = [];
      for (const usuarioId of aInsertar) {
        const usuarioRows = await supabase.select("usuarios", { filters: { id: usuarioId } });
        const cargoReal = usuarioRows?.[0]?.cargo;
        filas.push({ cliente_id: Number(clienteId), usuario_id: usuarioId, cargo: cargoReal || "" });
      }
      await supabase.insert(tabla, filas);
    } catch (error) {
      log.error("sincronizarContactosCliente: insert", error);
      throw error;
    }
  }

  return { borrados: aBorrar, insertados: aInsertar };
};

/**
 * Lector ÚNICO de `clientes_contactos_cliente`/`clientes_contactos_agencia` para superficies que
 * necesitan TODOS los contactos de TODOS los clientes de un país, sin tope de 4 (10.229 — Proyecto
 * Contactos, Fase 2, CRM). Trae las dos tablas completas (mismo patrón ya usado en esta pantalla
 * para leerlas una sola vez y repartir client-side por `cliente_id`, en vez de N queries) y arma
 * dos `Map<cliente_id, usuario_id[]>` — el orden de cada array es por `id` ASCENDENTE (orden de
 * alta), NUNCA por cargo (cargo ya es texto libre desde la Fase 1). Lado agencia excluye SIEMPRE
 * `cargo === "OMNET"` (contacto especial que administra Mapa de Calor aparte,
 * `TablaCalorAgencias.js` — mismo criterio que `sincronizarContactosCliente`, arriba).
 *
 * Devuelve solo `usuario_id` (nunca nombre/email) — el caller ya tiene `usuarios` cargado en
 * memoria (10.7: comparar/resolver por ID, no duplicar el fetch de usuarios acá).
 */
export const fetchTodosLosContactosPorLado = async () => {
  const [filasCliente, filasAgenciaRaw] = await Promise.all([
    supabase.select("clientes_contactos_cliente", { orderBy: "id.asc" }),
    supabase.select("clientes_contactos_agencia", { orderBy: "id.asc" }),
  ]);
  const filasAgencia = (filasAgenciaRaw || []).filter((f) => f.cargo !== OMNET_CARGO);

  const agruparPorCliente = (filas) => {
    const mapa = new Map();
    (filas || []).forEach((f) => {
      const clienteId = Number(f.cliente_id);
      if (!mapa.has(clienteId)) mapa.set(clienteId, []);
      mapa.get(clienteId).push(Number(f.usuario_id));
    });
    return mapa;
  };

  return { cliente: agruparPorCliente(filasCliente), agencia: agruparPorCliente(filasAgencia) };
};
